import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { LockClosedIcon, EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { authService } from '@/services/auth';
import { useLanguage } from '@/state/language/LanguageProvider';
import { useAuth } from '@/state/auth/AuthProvider';
import { PublicHeader } from '@/components/public/PublicHeader';

export const ResetPasswordPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { translateWithFallback } = useLanguage();
  const { setUser, setTokenState } = useAuth();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isValidToken, setIsValidToken] = useState(true);

  const token = searchParams.get('token');

  const tf = (key: string, fallback: string) => translateWithFallback(key, fallback);

  useEffect(() => {
    if (!token) {
      setIsValidToken(false);
      toast.error(tf('auth.invalidResetLink', 'Invalid or missing password reset link.'));
    }
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!token) {
      toast.error(tf('auth.invalidResetLink', 'Invalid or missing password reset link.'));
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error(tf('auth.passwordsDoNotMatch', 'Passwords do not match'));
      return;
    }

    if (newPassword.length < 8) {
      toast.error(
        tf('auth.passwordTooShort', 'Password must be at least 8 characters long')
      );
      return;
    }

    setIsLoading(true);

    try {
      const authData = await authService.resetPassword(token, newPassword);

      // Auto-sign-in after reset so require_password_change cannot loop the user
      if (authData?.token) {
        setTokenState(authData.token);
      }
      if (authData?.user) {
        setUser(authData.user);
      }

      toast.success(
        tf(
          'auth.passwordResetSuccessful',
          'Password reset successful. You are now signed in.'
        )
      );
      navigate('/dashboard', { replace: true });
    } catch (error) {
      console.error('Password reset error:', error);
      toast.error(
        tf(
          'auth.failedToResetPassword',
          'Failed to reset password. The link may be invalid or expired.'
        )
      );
    } finally {
      setIsLoading(false);
    }
  };

  if (!isValidToken) {
    return (
      <div className="reset-password-page min-h-screen bg-gray-100 flex flex-col">
        <PublicHeader />

        <div className="flex-1 flex items-center justify-center px-4 py-12">
          <div className="max-w-md w-full space-y-8 text-center">
            <div>
              <h1 className="text-4xl font-bold text-[#1a5d2e] mb-2">
                {tf('auth.invalidResetLinkTitle', 'Invalid Reset Link')}
              </h1>
              <p className="text-lg text-gray-700 mb-6">
                {tf(
                  'auth.invalidResetLinkMessage',
                  'This password reset link is invalid or has expired. Please request a new one.'
                )}
              </p>
              <Link
                to="/forgot-password"
                className="inline-block bg-[#1a5d2e] text-white py-3 px-6 rounded-lg font-semibold text-lg hover:bg-[#0d5d2c] transition-colors"
              >
                {tf('auth.requestNewResetLink', 'Request New Reset Link')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="reset-password-page min-h-screen bg-gray-100 flex flex-col">
      <PublicHeader />

      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="max-w-md w-full space-y-8 text-center">
          <div>
            <h1 className="text-4xl font-bold text-[#1a5d2e] mb-2">
              {tf('auth.resetPassword', 'Reset Password')}
            </h1>
            <p className="text-lg text-gray-700">
              {tf('auth.enterNewPasswordBelow', 'Enter your new password below')}
            </p>
          </div>

          <div className="flex justify-center">
            <div className="w-20 h-20 bg-[#1a5d2e] rounded-full flex items-center justify-center">
              <LockClosedIcon className="h-10 w-10 text-white" />
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="flex items-center space-x-3">
              <LockClosedIcon className="h-5 w-5 text-[#1a5d2e] flex-shrink-0" />
              <div className="flex-1 relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder={tf('auth.enterNewPassword', 'Enter new password')}
                  className="w-full py-3 px-3 pr-10 border-2 border-[#1a5d2e] rounded-lg bg-white text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#1a5d2e] focus:border-transparent text-center"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[#1a5d2e] hover:text-[#0d5d2c]"
                >
                  {showPassword ? (
                    <EyeSlashIcon className="h-5 w-5" />
                  ) : (
                    <EyeIcon className="h-5 w-5" />
                  )}
                </button>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <LockClosedIcon className="h-5 w-5 text-[#1a5d2e] flex-shrink-0" />
              <div className="flex-1 relative">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder={tf('auth.confirmNewPassword', 'Confirm new password')}
                  className="w-full py-3 px-3 pr-10 border-2 border-[#1a5d2e] rounded-lg bg-white text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#1a5d2e] focus:border-transparent text-center"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[#1a5d2e] hover:text-[#0d5d2c]"
                >
                  {showConfirmPassword ? (
                    <EyeSlashIcon className="h-5 w-5" />
                  ) : (
                    <EyeIcon className="h-5 w-5" />
                  )}
                </button>
              </div>
            </div>

            <div className="text-left text-sm text-gray-700">
              <p>
                {tf(
                  'auth.passwordRequirements',
                  'Use 8 or more characters with uppercase, lowercase, and a number.'
                )}
              </p>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-[#1a5d2e] text-white py-3 px-4 rounded-lg font-semibold text-lg hover:bg-[#0d5d2c] focus:outline-none focus:ring-2 focus:ring-[#1a5d2e] focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading
                ? tf('auth.resetting', 'Resetting...')
                : tf('auth.resetPasswordButton', 'Reset Password')}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};
